import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { TextInput } from "../../components/TextInput";
import { gql, useMutation, useQuery } from "@apollo/client";
import { SITES } from "./Sites";
// import { USERS } from "./Users";
import { toast } from "react-toastify";
import { Testing } from "../Testing/Testing";
import { Permanents } from "../Permanent/Permanents";
import { Tariffs } from "./Tariffs";
import { Capacities } from "./Capacities";
import { Status } from "./Status";
import { Products } from "./Products";
import { Permissions } from "./Permissions";
import { Transactions } from "./Transactions";
import { useAuth } from "../../auth/authContext";
import { apiUrl } from "../../config";
import { AddressFinder } from "../../components/AddressFinder";
import { GetSiteQuery, GetSiteQueryVariables } from "../../gql/graphql";
import AuditInfo from "../../components/AuditInfo";
import Behaviours from "./Behaviours";
import { DeleteModal } from "../../components/DeleteModal";

const GET = gql`
  query GetSite($id: UUID!) {
    sites(where: { id: { eq: $id } }) {
      items {
        id
        name
        companyId
        apiKey
        daysToStoreMessages
        isActive
        isListed
        address
        addressMetadata
        lgaName
        welcomeMessage
        gracePeriod
        reservationBuffer
        timeZone
        notificationEmails
        notificationPhones
        immediateNotifications
        behaviourConfig
        areas {
          id
          name
          capacity
        }
        createdUtc
        createdBy
        modifiedUtc
        modifiedBy
      }
    }
    companies(where: { isActive: { eq: true } }, order: { name: ASC }) {
      items {
        id
        name
        isActive #need to select values in where clause
      }
    }
  }
`;

const UPDATE = gql`
  mutation UpdateSite($site: SiteInput!) {
    updateSite(site: $site) {
      id
      companyId
      name
      apiKey
      daysToStoreMessages
      isActive
      isListed
      address
      addressMetadata
      lgaName
      welcomeMessage
      gracePeriod
      reservationBuffer
      timeZone
      notificationEmails
      notificationPhones
      immediateNotifications
      behaviourConfig
      company {
        id
        name
      }
      createdUtc
      createdBy
      modifiedUtc
      modifiedBy
    }
  }
`;
type SiteType = NonNullable<
  NonNullable<GetSiteQuery["sites"]>["items"]
>[number];

export const Site = () => {
  let { id, tab } = useParams();
  const { roles } = useAuth();

  const navigate = useNavigate();

  const [validAddress, setValidAddress] = useState(false);
  const [site, setSite] = useState<SiteType | null>(null);
  const { data: timeZones } = useQuery<{ timeZones: string[] }>(
    gql`
      query {
        timeZones
      }
    `
  );
  const { data } = useQuery<GetSiteQuery, GetSiteQueryVariables>(GET, {
    variables: {
      id: id === "new" ? "00000000-0000-0000-0000-000000000000" : id,
    },
    fetchPolicy: "no-cache",
  });

  const [updateSite, { loading: updating }] = useMutation(UPDATE, {
    refetchQueries: id === "new" ? [{ query: SITES }] : [],
    awaitRefetchQueries: true,
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (site && site.addressMetadata !== "{}") {
      setValidAddress(true);
    } else {
      setValidAddress(false);
    }
  }, [site]);

  useEffect(() => {
    if (id === "new") {
      setSite({
        id: "00000000-0000-0000-0000-000000000000",
        companyId: null,
        name: "",
        isActive: true,
        daysToStoreMessages: 14,
        gracePeriod: 15,
        reservationBuffer: 1,
        isListed: false,
        immediateNotifications: false,
      });
    }
    if (data?.sites?.items?.length === 1) {
      var metaData = data.sites.items[0]?.addressMetadata
        ? JSON.parse(data.sites.items[0].addressMetadata)
        : JSON.parse("{}");

      var addressMetadata = JSON.stringify(metaData);

      if (data.sites.items[0]) {
        setSite({
          ...data.sites.items[0],
          addressMetadata: addressMetadata,
        });
      }

      var s = { ...data.sites.items[0] };
      delete (s as any).__typename;
      reset(s);
    }
  }, [id, data, reset]);

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    if (!site) return;
    //console.log(data);
    let update = {
      id: site.id,
      ...data,
      addressMetadata: site.addressMetadata,
      daysToStoreMessages: parseInt(data.daysToStoreMessages),
      gracePeriod: parseInt(data.gracePeriod),
      reservationBuffer: parseInt(data.reservationBuffer),
      isActive: site.isActive,
      isListed: site.isListed,
      immediateNotifications: data.immediateNotifications ?? false,
      areas: [],
    };

    updateSite({ variables: { site: update } }).then((x) => {
      if (x.data.updateSite) {
        toast(`${x.data.updateSite.name} saved`, {
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
        });
        navigate("/sites");
      }
    });
  };

  return (
    <>
      {site && (
        <>
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
            <h1 className="h2">
              {site.name ? <>{site.name}</> : <>Add Site</>}
              {!site.isActive && (
                <span className="badge bg-secondary ms-2">Inactive</span>
              )}
            </h1>
          </div>
          <AuditInfo
            entity={
              site as {
                createdBy?: string;
                createdUtc?: string | null;
                modifiedBy?: string;
                modifiedUtc?: string | null;
              }
            }
          />

          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <Link
                className={`nav-link ${!tab ? "active" : ""}`}
                to={`/sites/${site.id}`}
              >
                Details
              </Link>
            </li>

            {site.id !== "00000000-0000-0000-0000-000000000000" && (
              <>
                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "status" ? "active" : ""}`}
                    to={`/sites/${site.id}/status`}
                  >
                    Status
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "transactions" ? "active" : ""}`}
                    to={`/sites/${site.id}/transactions`}
                  >
                    Transactions
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "permissions" ? "active" : ""
                      }`}
                    to={`/sites/${site.id}/permissions`}
                  >
                    Permissions
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "tariffs" ? "active" : ""}`}
                    to={`/sites/${site.id}/tariffs`}
                  >
                    Tariffs
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "capacities" ? "active" : ""
                      }`}
                    to={`/sites/${site.id}/capacities`}
                  >
                    Capacities
                  </Link>
                </li>

                {/* <li className="nav-item">
                        <Link className={`nav-link ${tab === 'vehicles' ? 'active' : ''}`} to={`/sites/${site.id}/vehicles`}>Vehicles</Link>
                    </li> 

                    <li className="nav-item">
                        <Link className={`nav-link ${tab === 'transactions' ? 'active' : ''}`} to={`/sites/${site.id}/transactions`}>Transactions</Link>
                    </li> */}

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "behaviours" ? "active" : ""
                      }`}
                    to={`/sites/${site.id}/behaviours`}
                  >
                    Behaviours
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "products" ? "active" : ""}`}
                    to={`/sites/${site.id}/products`}
                  >
                    Products
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "permanent" ? "active" : ""
                      }`}
                    to={`/sites/${site.id}/permanent`}
                  >
                    Permanent
                  </Link>
                </li>

                {/* <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "changes" ? "active" : ""}`}
                    to={`/sites/${site.id}/changes`}
                  >
                    Changes
                  </Link>
                </li> */}

                {roles.includes("test:execute") && (
                  <li className="nav-item">
                    <Link
                      className={`nav-link text-danger ${tab === "testing" ? "active" : ""
                        }`}
                      to={`/sites/${site.id}/testing`}
                    >
                      Testing
                    </Link>
                  </li>
                )}
              </>
            )}
          </ul>

          {!tab && (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className={
                "needs-validation" +
                (Object.keys(errors).length > 0 ? " was-validated" : "")
              }
              noValidate
            >
              <div className="row">
                <div className="col-md">
                  <TextInput
                    name="name"
                    label="Name"
                    defaultValue={site.name ?? ""}
                    placeholder="Name"
                    register={register}
                    config={{ required: true }}
                    errors={errors}
                    readOnly={!roles.includes("site:edit")}
                    disabled={!roles.includes("site:edit")}
                  />
                </div>
                <div className="col-md">
                  <label className="required form-label" htmlFor="companyId">
                    Company
                  </label>
                  <select
                    className="form-select"
                    defaultValue={site.companyId}
                    {...register("companyId", { required: true })}
                    required
                    disabled={!roles.includes("site:edit")}
                  >
                    <option value="">Choose a company</option>
                    {data &&
                      data.companies?.items?.map((company) => (
                        <option key={company?.id} value={company?.id}>
                          {company?.name}
                        </option>
                      ))}
                  </select>
                  <div className="invalid-feedback">
                    Please choose a company, or add one if needed.
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md">
                  <AddressFinder
                    enabled={!!site}
                    onAddressSelect={(fullAddress, metaData) => {
                      setSite({
                        ...site,
                        addressMetadata: JSON.stringify(metaData),
                      });
                      setValue("address", fullAddress);
                    }}
                    onLocationSelect={(fullLocation, metaData) => {
                      setSite({
                        ...site,
                        addressMetadata: JSON.stringify(metaData),
                      });
                    }}
                    id="address"
                    name="address"
                    label="Address"
                    defaultValue={site.address ?? ""}
                    placeholder="Address"
                    register={register}
                    required={false}
                    errors={errors}
                    readOnly={!roles.includes("site:edit")}
                    disabled={!roles.includes("site:edit")}
                  />
                </div>
                <div className="col-md">
                  <label className="required form-label" htmlFor="timeZone">
                    Time Zone
                  </label>
                  <select
                    className="form-control"
                    id="timeZone"
                    defaultValue={site.timeZone ?? ""}
                    {...register("timeZone", { required: true })}
                    required
                    disabled={!roles.includes("site:edit")}
                  >
                    <option value="">Choose a timezone</option>
                    {timeZones?.timeZones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                  <div className="invalid-feedback">
                    Please choose a timezone for this site.
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md">
                  <input
                    type="checkbox"
                    className="btn-check"
                    id="isListed"
                    autoComplete="off"
                    onClick={() =>
                      setSite({ ...site, isListed: !site.isListed })
                    }
                    readOnly={!roles.includes("site:edit")}
                    disabled={!roles.includes("site:edit")}
                  />
                  <label
                    className={
                      site.isListed && validAddress
                        ? "btn btn-success"
                        : "btn btn-danger"
                    }
                    htmlFor="isListed"
                  >
                    {site.isListed && validAddress
                      ? "Listed Publicly"
                      : "Not Listed"}
                  </label>

                  {!validAddress && (
                    <div className="text-danger">
                      If you want to publicly list this site, please enter an
                      address
                    </div>
                  )}
                </div>
              </div>

              <div className="card mt-3">
                <div className="card-body">
                  <h6 className="card-title">SenseIQ</h6>

                  <div className="alert alert-info">
                    Connection URL:{" "}
                    <strong>https://{apiUrl}/shareparkHub</strong>
                  </div>

                  <div className="row">
                    <div className="col">
                      <TextInput
                        name="apiKey"
                        label="API Key"
                        defaultValue={site.apiKey ?? ""}
                        placeholder="Guid from Sharepark v1/Apikey for v2"
                        register={register}
                        errors={errors}
                        readOnly={!roles.includes("site:edit")}
                        disabled={!roles.includes("site:edit")}
                      />
                    </div>
                    <div className="col">
                      <TextInput
                        name="daysToStoreMessages"
                        type="number"
                        label="Days to store messages"
                        defaultValue={site.daysToStoreMessages}
                        placeholder="# days to keep messages"
                        register={register}
                        config={{ required: true }}
                        errors={errors}
                        readOnly={!roles.includes("site:edit")}
                        disabled={!roles.includes("site:edit")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md">
                  <TextInput
                    name="gracePeriod"
                    type="number"
                    label="Grace Period (mins)"
                    defaultValue={site.gracePeriod}
                    placeholder="# mins of Grace"
                    register={register}
                    config={{ required: true }}
                    errors={errors}
                    readOnly={!roles.includes("site:edit")}
                    disabled={!roles.includes("site:edit")}
                  />
                  <TextInput
                    name="reservationBuffer"
                    type="number"
                    label="Reservation Buffer (hours)"
                    defaultValue={site.reservationBuffer}
                    placeholder="# hours after a Reservation to block/buffer a bay"
                    register={register}
                    config={{ required: true }}
                    errors={errors}
                    readOnly={!roles.includes("site:edit")}
                    disabled={!roles.includes("site:edit")}
                  />
                </div>
              </div>

              {/* <div className="row mt-3">
                <div className="col">
                  <TextInput
                    name="welcomeMessage"
                    label="Welcome Message"
                    defaultValue={site.welcomeMessage ?? ""}
                    placeholder="put custom welcome message here"
                    register={register}
                    errors={errors}
                    readOnly={!roles.includes("site:edit")}
                    disabled={!roles.includes("site:edit")}
                  />

                  <div className="alert alert-info">
                    [firstname], [lastname] or [plate] will be replaced
                    automatically. SenseIq can also use [bay].
                  </div>
                </div>
                <div className="col">
                  <div className="card">
                    <div className="card-body">
                      <h6 className="card-title">
                        Notifications (Abnormal Events)
                      </h6>

                      <div className="form-group">
                        <label htmlFor="notificationEmails">
                          Email Addresses (semicolon separated)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="notificationEmails"
                          defaultValue={site.notificationEmails ?? ""}
                          {...register("notificationEmails")}
                          readOnly={!roles.includes("site:edit")}
                          disabled={!roles.includes("site:edit")}
                        />
                      </div>
                      <div className="form-group mt-4">
                        <label htmlFor="notificationPhones">
                          Phone Numbers (semicolon separated)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="notificationPhones"
                          defaultValue={site.notificationPhones ?? ""}
                          {...register("notificationPhones")}
                          readOnly={!roles.includes("site:edit")}
                          disabled={!roles.includes("site:edit")}
                        />
                      </div>

                      <div className="form-check mt-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="immediateNotifications"
                          defaultChecked={site.immediateNotifications}
                          {...register("immediateNotifications")}
                          readOnly={!roles.includes("site:edit")}
                          disabled={!roles.includes("site:edit")}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="immediateNotifications"
                        >
                          Send immediate notifications
                        </label>
                      </div>
                      <div className="alert alert-info mt-2">
                        If checked, notifications will be sent immediately after
                        a reservation is made. If unchecked, notifications will
                        be sent as a daily digest.
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}

              {roles.includes("site:edit") && (
                <div className="mb-3 d-flex justify-content-between">
                  <div>
                    <input
                      disabled={updating}
                      className="btn btn-primary"
                      type="submit"
                      value="Save"
                    />{" "}
                    <Link to={"/sites"} className="btn btn-link">
                      Cancel
                    </Link>
                  </div>
                  {id !== "new" && !site.isActive && (
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={() => {
                        updateSite({
                          variables: {
                            site: {
                              id: site.id,
                              companyId: site.companyId,
                              name: site.name,
                              address: site.address,
                              addressMetadata: site.addressMetadata,
                              lgaName: site.lgaName,
                              welcomeMessage: site.welcomeMessage,
                              gracePeriod: site.gracePeriod,
                              reservationBuffer: site.reservationBuffer,
                              timeZone: site.timeZone,
                              notificationEmails: site.notificationEmails,
                              notificationPhones: site.notificationPhones,
                              immediateNotifications: site.immediateNotifications,
                              daysToStoreMessages: site.daysToStoreMessages,
                              isListed: site.isListed,
                              isActive: true,
                            },
                          },
                        }).then((x) => {
                          if (x.data.updateSite) {
                            toast("Site restored", {
                              type: toast.TYPE.SUCCESS,
                              autoClose: 3000,
                            });
                            navigate("/sites");
                          }
                        });
                      }}
                    >
                      Restore
                    </button>
                  )}
                  {id !== "new" && site.isActive && (
                    <DeleteModal
                      title="Site"
                      onConfirm={() => {
                        updateSite({
                          variables: {
                            site: {
                              id: site.id,
                              companyId: site.companyId,
                              name: site.name,
                              address: site.address,
                              addressMetadata: site.addressMetadata,
                              lgaName: site.lgaName,
                              welcomeMessage: site.welcomeMessage,
                              gracePeriod: site.gracePeriod,
                              reservationBuffer: site.reservationBuffer,
                              timeZone: site.timeZone,
                              notificationEmails: site.notificationEmails,
                              notificationPhones: site.notificationPhones,
                              immediateNotifications: site.immediateNotifications,
                              daysToStoreMessages: site.daysToStoreMessages,
                              isListed: site.isListed,
                              isActive: false,
                            },
                          },
                        }).then((x) => {
                          if (x.data.updateSite) {
                            toast("Site deleted", {
                              type: toast.TYPE.SUCCESS,
                              autoClose: 3000,
                            });
                            navigate("/sites");
                          }
                        });
                      }}
                    >
                      <dl className="mb-0">
                        <dt>Name</dt>
                        <dd>{site.name}</dd>
                        {site.address && (
                          <>
                            <dt>Address</dt>
                            <dd>{site.address}</dd>
                          </>
                        )}
                        {site.areas && site.areas.length > 0 && (
                          <>
                            <dt>Areas</dt>
                            <dd>{site.areas.map(a => a?.name).join(", ")}</dd>
                          </>
                        )}
                      </dl>
                    </DeleteModal>
                  )}
                </div>
              )}
            </form>
          )}

          {tab === "status" && <Status site={site} />}

          {tab === "transactions" && <Transactions site={site} />}

          {tab === "products" && <Products site={site} />}

          {tab === "permissions" && <Permissions site={site} />}

          {tab === "behaviours" &&
            site.id !== "00000000-0000-0000-0000-000000000000" && (
              <Behaviours
                site={site}
                onSave={updateSite}
                isSaving={updating}
                canEdit={roles.includes("site:edit")}
              />
            )}

          {tab === "tariffs" && <Tariffs site={site} />}

          {tab === "capacities" && <Capacities site={site} />}

          {tab === "permanent" && <Permanents siteId={site.id} />}

          {/* {tab === "changes" && (
            <AuditEvents entityType={"Site"} entityId={site.id} />
          )} */}

          {tab === "testing" && <Testing siteId={site.id} />}
        </>
      )}
    </>
  );
};
